/**
 * Client-side pixel geometry (mirrors the Worker's lib/porch-review/geometry.ts).
 * Canonical bounds: integer source pixels, left/top inclusive, right/bottom exclusive.
 */
export interface PixelBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Size {
  width: number;
  height: number;
}

export function boundsFromCorners(x0: number, y0: number, x1: number, y1: number): PixelBounds {
  return {
    left: Math.floor(Math.min(x0, x1)),
    top: Math.floor(Math.min(y0, y1)),
    right: Math.ceil(Math.max(x0, x1)),
    bottom: Math.ceil(Math.max(y0, y1)),
  };
}

export function clampToImage(b: PixelBounds, size: Size): PixelBounds {
  const c = (v: number, hi: number) => Math.min(Math.max(0, v), hi);
  return {
    left: c(b.left, size.width),
    top: c(b.top, size.height),
    right: c(b.right, size.width),
    bottom: c(b.bottom, size.height),
  };
}

export function boundsWidth(b: PixelBounds): number {
  return b.right - b.left;
}

export function boundsHeight(b: PixelBounds): number {
  return b.bottom - b.top;
}

/** Null when valid, otherwise the reason shown to the reviewer. */
export function validateBounds(b: PixelBounds, size: Size, minSidePx: number): string | null {
  if (![b.left, b.top, b.right, b.bottom].every(Number.isInteger)) return "Bounds must be integers.";
  if (b.right <= b.left || b.bottom <= b.top) return "Box has zero size.";
  if (b.left < 0 || b.top < 0 || b.right > size.width || b.bottom > size.height) return "Box is outside the image.";
  if (boundsWidth(b) < minSidePx || boundsHeight(b) < minSidePx) return `Box is smaller than ${minSidePx}px; draw a larger box.`;
  return null;
}

/** Translate a box by (dx, dy) image pixels, keeping its size, clamped inside the image. */
export function moveBounds(b: PixelBounds, dx: number, dy: number, size: Size): PixelBounds {
  const w = boundsWidth(b);
  const h = boundsHeight(b);
  const left = Math.min(Math.max(0, Math.round(b.left + dx)), size.width - w);
  const top = Math.min(Math.max(0, Math.round(b.top + dy)), size.height - h);
  return { left, top, right: left + w, bottom: top + h };
}

export type Handle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";
export const HANDLES: Handle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

/** Resize by dragging a handle to an image-space point; any direction, normalized after. */
export function resizeBounds(b: PixelBounds, handle: Handle, px: number, py: number, size: Size): PixelBounds {
  let { left, top, right, bottom } = b;
  const x = Math.round(px);
  const y = Math.round(py);
  if (handle.includes("w")) left = x;
  if (handle.includes("e")) right = x;
  if (handle.includes("n")) top = y;
  if (handle.includes("s")) bottom = y;
  return clampToImage(boundsFromCorners(left, top, right, bottom), size);
}

export function containsPoint(b: PixelBounds, x: number, y: number): boolean {
  return x >= b.left && x < b.right && y >= b.top && y < b.bottom;
}

export function handlePoint(b: PixelBounds, h: Handle): { x: number; y: number } {
  const cx = (b.left + b.right) / 2;
  const cy = (b.top + b.bottom) / 2;
  switch (h) {
    case "nw":
      return { x: b.left, y: b.top };
    case "n":
      return { x: cx, y: b.top };
    case "ne":
      return { x: b.right, y: b.top };
    case "e":
      return { x: b.right, y: cy };
    case "se":
      return { x: b.right, y: b.bottom };
    case "s":
      return { x: cx, y: b.bottom };
    case "sw":
      return { x: b.left, y: b.bottom };
    case "w":
      return { x: b.left, y: cy };
  }
}

export function sameBounds(a: PixelBounds, b: PixelBounds): boolean {
  return a.left === b.left && a.top === b.top && a.right === b.right && a.bottom === b.bottom;
}
